import { action } from '@ember/object';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { sortByProperties } from 'donor-elf-web/lib/array-utils';
import { service } from '@ember/service';
import JournalContactInfo from 'donor-elf-web/lib/models/journal-contact-info';
import Changeset from 'ember-changeset';
import lookupValidator from 'ember-changeset-validations';
import SegmentNameValidation from 'donor-elf-web/validations/journal/segment-name';

export interface SegmentAfterSaveArgs {
  segmentName: string;
  nameChanged?: boolean;
  deleted?: boolean;
}
interface JournalSegmentsEditModalArgs {
  allSegments: string[];
  segmentName: string;
  journalContactInfos: JournalContactInfo[];
  onToggleFilter: (segment: string) => void;
  onAfterSave: (args: SegmentAfterSaveArgs) => void;
  onCloseRequested?: () => void;
}

class SegmentRow {
  jcInfo: JournalContactInfo;
  @tracked selected: boolean;

  constructor(jcInfo: JournalContactInfo, selected: boolean) {
    this.jcInfo = jcInfo;
    this.selected = selected;
  }
}

export default class JournalSegmentsEditModalComponent extends Component<JournalSegmentsEditModalArgs> {
  constructor(owner: any, args: JournalSegmentsEditModalArgs) {
    super(owner, args);

    this.rows = sortByProperties(
      this.args.journalContactInfos.map((jcInfo) => {
        return new SegmentRow(
          jcInfo,
          jcInfo.journalContact.segments.some(
            (x) => x === this.args.segmentName,
          ),
        );
      }),
      ['selected:desc', 'jcInfo.contact.name:asc'],
    );
  }
  @service declare contactsCache: any;

  @tracked q = '';
  @tracked filterGroup = '';
  @tracked filterSegment = '';
  @tracked rows: SegmentRow[] = [];
  @tracked saving = false;
  @tracked error?: any;
  @tracked editingSegmentName = false;
  @tracked editNameChangeset?: any;
  @tracked editNameShowValidationErrors = false;

  get filteredRows() {
    let rows = this.rows.slice();
    if (this.q.trim()?.length >= 2) {
      let qCaseFilter = this.q.toLowerCase();
      rows = rows.filter((row) => {
        return row.jcInfo.contact.nameLowerCase.includes(qCaseFilter);
      });
    }
    if (this.filterGroup !== '') {
      rows = rows.filter((row) => {
        return row.jcInfo.contact.groups?.some((x) => x === this.filterGroup);
      });
    }
    if (this.filterSegment !== '') {
      rows = rows.filter((row) => {
        return row.jcInfo.journalContact.segments?.some(
          (x) => x === this.filterSegment,
        );
      });
    }
    return rows;
  }

  get selectedCount() {
    return this.rows.filter((x) => x.selected).length;
  }

  get allFilteredRowsSelected() {
    return !this.filteredRows.some((x) => x.selected === false);
  }

  get groupSelectOptions() {
    return [
      {
        val: '',
        label: 'Any',
      },
    ].concat(
      this.contactsCache.sortedGroups.map((x: string) => {
        return { val: x, label: x };
      }),
    );
  }

  get segmentSelectOptions() {
    return [
      {
        val: '',
        label: 'Any',
      },
    ].concat(
      this.args.allSegments.map((x: string) => {
        return { val: x, label: x };
      }),
    );
  }

  @action
  handleEditSegmentName() {
    this.editingSegmentName = true;
    this.editNameShowValidationErrors = false;
    this.editNameChangeset = new Changeset(
      { name: this.args.segmentName },
      lookupValidator(SegmentNameValidation),
      SegmentNameValidation,
    );
  }

  @action
  handleToggleSelectAllFilteredRows() {
    let selectedVal = !this.allFilteredRowsSelected;
    this.filteredRows.forEach((row) => {
      row.selected = selectedVal;
    });
  }

  @action
  handleSearchInput(e: any) {
    this.q = e.target.value?.trim();
  }

  @action
  handleFilterGroupChanged(group: string) {
    this.filterGroup = group;
  }

  @action
  handleFilterSegmentChanged(segment: string) {
    this.filterSegment = segment;
  }

  @action
  toggleSelected(row: SegmentRow) {
    row.selected = !row.selected;
  }

  @action
  async save() {
    if (this.saving) {
      return;
    }
    this.error = undefined;
    this.saving = true;
    let existingSegmentName = this.args.segmentName;
    let existingOrNewSegmentName = existingSegmentName; // will be new name if the name was edited

    if (this.editNameChangeset) {
      await this.editNameChangeset.validate();
      if (!this.editNameChangeset.get('isValid')) {
        this.editNameShowValidationErrors = true;
        this.saving = false;
        return;
      }
      existingOrNewSegmentName = this.editNameChangeset.get('name');
    }
    let segmentNameChanged = existingOrNewSegmentName !== existingSegmentName;

    try {
      for (let i = 0; i < this.rows.length; i++) {
        let row = this.rows[i];
        if (row.selected) {
          if (
            !row.jcInfo.journalContact.segments.some(
              (x) => x === existingOrNewSegmentName,
            )
          ) {
            row.jcInfo.journalContact.segments.push(existingOrNewSegmentName);
            await row.jcInfo.journalContact.save();
          }
        } else {
          if (
            row.jcInfo.journalContact.segments.some(
              (x) => x === existingOrNewSegmentName,
            )
          ) {
            row.jcInfo.journalContact.segments =
              row.jcInfo.journalContact.segments.filter(
                (x) => x !== existingOrNewSegmentName,
              );
            await row.jcInfo.journalContact.save();
          }
        }
        // takes care of scenario where segment name was changed and previously selected journalContact was unselected
        if (
          segmentNameChanged &&
          row.jcInfo.journalContact.segments.some(
            (x) => x === existingSegmentName,
          )
        ) {
          row.jcInfo.journalContact.segments =
            row.jcInfo.journalContact.segments.filter(
              (x) => x !== existingSegmentName,
            );
          await row.jcInfo.journalContact.save();
        }
      }
      this.args.onAfterSave({
        segmentName: existingOrNewSegmentName,
        nameChanged: segmentNameChanged,
      });
    } catch (error) {
      this.error = error;
    }
    this.saving = false;
  }

  @action
  async delete() {
    if (this.saving) {
      return;
    }
    this.error = undefined;
    this.saving = true;
    let segmentName = this.args.segmentName;

    try {
      for (let i = 0; i < this.rows.length; i++) {
        let row = this.rows[i];
        if (row.jcInfo.journalContact.segments.some((x) => x === segmentName)) {
          row.jcInfo.journalContact.segments =
            row.jcInfo.journalContact.segments.filter((x) => x !== segmentName);
          await row.jcInfo.journalContact.save();
        }
      }
      this.args.onAfterSave({
        segmentName: segmentName,
        deleted: true,
      });
    } catch (error) {
      this.error = error;
    }
    this.saving = false;
  }

  @action
  handleCloseRequested() {
    if (!this.saving) {
      this.args.onCloseRequested?.();
    }
  }
}
